import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { X, Plus, Minus, Trash2, MessageCircle, ShoppingBag } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { buildWhatsAppUrl } from "@/data/translations";
import { formatPrice, localized, productSlug, localizedColor } from "@/data/products";
import StoreImg from "@/components/store/StoreImg";
import CheckoutModal from "@/components/store/CheckoutModal";
import { trackEvent } from "@/lib/analytics";

export default function CartDrawer() {
  const { t, lang, isRTL } = useLanguage();
  const S = t.store;
  const { isOpen, closeCart, detailedItems, totalItems, subtotal, shippingFee, total, updateQty, removeFromCart, clearCart, autoClampSignal } = useCart();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const previouslyOpenRef = useRef(false);

  // Whenever the stock-aware CartContext auto-clamps a line down (e.g., on
  // first mount after the live stock map loads with reduced inventory), let
  // the user know — once per clamp event.
  useEffect(() => {
    if (autoClampSignal > 0) {
      toast.warning(S.maxStockReached);
    }
  }, [autoClampSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isOpen && !previouslyOpenRef.current) {
      trackEvent("cart_view", {
        routeName: "cart_drawer",
        metadata: {
          items_count: totalItems,
          subtotal,
          shipping_fee: shippingFee,
          total,
        },
      });
    }
    previouslyOpenRef.current = isOpen;
  }, [isOpen, shippingFee, subtotal, total, totalItems]);

  // Lock body scroll while drawer is open
  useEffect(() => {
    if (isOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [isOpen]);

  const handleCheckout = () => {
    if (!detailedItems.length) return;
    trackEvent("checkout_start", {
      routeName: "cart_drawer",
      metadata: {
        items_count: totalItems,
        subtotal,
        shipping_fee: shippingFee,
        total,
      },
    });
    setCheckoutOpen(true);
  };

  const handleWhatsAppHelp = () => {
    const helpMsg =
      "Hi BOB Services LB, I need help with my order from the store.";
    trackEvent("whatsapp_click", {
      routeName: "cart_drawer",
      metadata: { source: "cart_help" },
    });
    trackEvent("contact_click", {
      routeName: "cart_drawer",
      metadata: { source: "cart_help", channel: "whatsapp" },
    });
    window.open(buildWhatsAppUrl(helpMsg), "_blank", "noopener,noreferrer");
  };

  const sideClass = isRTL
    ? `${isOpen ? "translate-x-0" : "-translate-x-full"} left-0`
    : `${isOpen ? "translate-x-0" : "translate-x-full"} right-0`;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-[80] transition-opacity ${
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={closeCart}
        data-testid="cart-backdrop"
        aria-hidden
      />

      {/* Drawer */}
      <aside
        className={`fixed top-0 ${sideClass} h-full w-full sm:w-[440px] bg-white text-[#111] z-[81] shadow-[0_0_60px_rgba(0,0,0,0.25)] transition-transform duration-300 flex flex-col dark:bg-[#080808] dark:text-[#F6F7FB] dark:shadow-[0_0_70px_rgba(0,0,0,0.65)]`}
        data-testid="cart-drawer"
        role="dialog"
        aria-label={S.cartTitle}
        aria-modal="true"
      >
        {/* Header */}
        <header className="flex items-center justify-between px-5 sm:px-6 h-16 border-b border-black/5 dark:border-white/10">
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-[#F37021]" />
            <h2 className="font-display text-lg font-bold text-[#111] dark:text-white">
              {S.cartTitle}
              {totalItems > 0 && (
                <span className="ms-2 text-sm font-medium text-[#888] dark:text-white/45">({totalItems})</span>
              )}
            </h2>
          </div>
          <button
            type="button"
            onClick={closeCart}
            className="w-10 h-10 rounded-full border border-black/10 hover:border-[#111] flex items-center justify-center transition-colors dark:border-white/12 dark:hover:border-white/45"
            data-testid="cart-close"
            aria-label="Close cart"
          >
            <X className="w-5 h-5 text-[#111] dark:text-white" />
          </button>
        </header>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-5">
          {detailedItems.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center gap-4">
              <div className="w-20 h-20 rounded-full bg-[#FDF1E8] flex items-center justify-center dark:bg-[#F37021]/14">
                <ShoppingBag className="w-9 h-9 text-[#F37021]" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-display text-lg font-bold text-[#111] dark:text-white">{S.cartEmpty}</h3>
                <p className="text-sm text-[#666] mt-1 dark:text-white/55">{S.cartEmptyHint}</p>
              </div>
              <Link
                to="/stock"
                onClick={closeCart}
                className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#F37021] hover:bg-[#E06015] text-white text-sm font-medium transition-colors"
                data-testid="cart-empty-cta"
              >
                {S.continueShopping}
              </Link>
            </div>
          ) : (
            <ul className="space-y-4">
              {detailedItems.map((i) => {
                const name = localized(i.product.name, lang);
                // Live remaining stock for this exact (size, color) variant
                // comes pre-computed from the stock-aware CartContext.
                const lineMax = i.maxQty;
                const atMax = i.qty >= lineMax;
                const colorLabel = i.color
                  ? localizedColor(i.product, i.color, lang)
                  : (i.product.color ? localized(i.product.color, lang) : "");
                return (
                  <li
                    key={i.key}
                    className="flex gap-3 items-start p-3 rounded-2xl bg-[#FAFAFA] border border-black/5 dark:bg-white/[0.055] dark:border-white/10"
                    data-testid={`cart-item-${i.id}`}
                  >
                    <Link
                      to={`/stock/${productSlug(i.id)}`}
                      onClick={closeCart}
                      className="shrink-0 w-20 h-24 rounded-xl overflow-hidden bg-[#0F0F0F] block"
                    >
                      <StoreImg src={i.product.images?.[0]} alt={name} width={200} className="w-full h-full object-cover" />
                    </Link>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#F37021]">
                          #{i.id}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeFromCart(i.key)}
                          className="text-[#999] hover:text-red-500 transition-colors dark:text-white/38 dark:hover:text-red-300"
                          data-testid={`cart-remove-${i.id}`}
                          aria-label="Remove"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <h4 className="font-display text-sm font-semibold text-[#111] leading-tight line-clamp-2 dark:text-white">
                        {name}
                      </h4>
                      <div className="mt-1 text-[11px] text-[#666] flex items-center gap-2 flex-wrap dark:text-white/55">
                        {i.size && (
                          <span className="px-2 py-0.5 rounded-full bg-white border border-black/10 dark:bg-black/35 dark:border-white/10">
                            {S.sizeLabel}: <span className="font-semibold text-[#111] dark:text-white">{i.size}</span>
                          </span>
                        )}
                        {colorLabel && (
                          <span className="px-2 py-0.5 rounded-full bg-white border border-black/10 dark:bg-black/35 dark:border-white/10">
                            {colorLabel}
                          </span>
                        )}
                      </div>
                      <div className="mt-1.5 flex items-baseline gap-1.5">
                        <span className="text-sm font-bold text-[#F37021]">{formatPrice(i.unitPrice)}</span>
                        <span className="text-[11px] text-[#999] line-through dark:text-white/35">
                          {formatPrice(i.product.originalPrice)}
                        </span>
                      </div>
                      <div className="mt-3 flex items-center justify-between gap-2">
                        <div className="inline-flex items-center rounded-full border border-black/10 bg-white dark:border-white/10 dark:bg-black/35">
                          <button
                            type="button"
                            onClick={() => updateQty(i.key, i.qty - 1)}
                            className="w-8 h-8 flex items-center justify-center text-[#111] hover:text-[#F37021] transition-colors dark:text-white dark:hover:text-[#F58D4A]"
                            aria-label="Decrease"
                            data-testid={`cart-qty-dec-${i.id}`}
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="min-w-[28px] text-center text-sm font-semibold text-[#111] dark:text-white" data-testid={`cart-qty-${i.id}`}>
                            {i.qty}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              if (atMax) {
                                toast.warning(S.maxStockReached);
                                return;
                              }
                              updateQty(i.key, i.qty + 1);
                            }}
                            disabled={atMax}
                            className="w-8 h-8 flex items-center justify-center text-[#111] hover:text-[#F37021] disabled:text-[#CCC] disabled:cursor-not-allowed transition-colors dark:text-white dark:hover:text-[#F58D4A] dark:disabled:text-white/20"
                            aria-label="Increase"
                            data-testid={`cart-qty-inc-${i.id}`}
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                        <span className="text-sm font-bold text-[#111] dark:text-white" data-testid={`cart-line-${i.id}`}>
                          {formatPrice(i.lineTotal)}
                        </span>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer */}
        {detailedItems.length > 0 && (
          <footer className="border-t border-black/5 p-5 sm:p-6 space-y-3 bg-white dark:border-white/10 dark:bg-[#080808]">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#666] dark:text-white/55">{S.subtotal}</span>
                <span className="font-medium text-[#111] dark:text-white" data-testid="cart-subtotal">
                  {formatPrice(subtotal)}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#666] dark:text-white/55">{S.shippingLebanon}</span>
                <span className="font-medium text-[#111] dark:text-white" data-testid="cart-shipping">
                  {formatPrice(shippingFee)}
                </span>
              </div>
              <div className="flex items-center justify-between pt-2 mt-1 border-t border-dashed border-black/10 dark:border-white/12">
                <span className="text-sm font-semibold text-[#111] dark:text-white">{S.total}</span>
                <span className="font-display text-xl font-bold text-[#F37021]" data-testid="cart-total">
                  {formatPrice(total)}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={handleCheckout}
              className="w-full h-13 py-3.5 rounded-full bg-[#F37021] hover:bg-[#E06015] text-white font-semibold inline-flex items-center justify-center gap-2 transition-colors shadow-[0_10px_24px_rgba(243,112,33,0.25)]"
              data-testid="cart-checkout"
            >
              <ShoppingBag className="w-5 h-5" />
              {S.checkoutCta}
            </button>
            <button
              type="button"
              onClick={handleWhatsAppHelp}
              className="w-full text-xs text-[#25D366] hover:text-[#1EBE5C] font-medium inline-flex items-center justify-center gap-1.5"
              data-testid="cart-whatsapp-help"
            >
              <MessageCircle className="w-3.5 h-3.5" />
              {S.helpOnWhatsapp}
            </button>
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={clearCart}
                className="text-xs text-[#999] hover:text-red-500 transition-colors dark:text-white/38 dark:hover:text-red-300"
                data-testid="cart-clear"
              >
                {S.clearCart}
              </button>
              <Link
                to="/stock"
                onClick={closeCart}
                className="text-xs font-semibold text-[#F37021] hover:text-[#E06015] transition-colors"
              >
                {S.continueShopping}
              </Link>
            </div>
          </footer>
        )}
      </aside>

      <CheckoutModal open={checkoutOpen} onClose={() => setCheckoutOpen(false)} />
    </>
  );
}
